import { isValidPhoneNumber } from "react-phone-number-input"
import * as Yup from "yup"

import useOTPRateLimit from "@hooks/useOTPRateLimit"

import { faIcon } from "@utils/faIcon"

import { Button } from "@atoms"

import { AlertBanner } from "@molecules"
import { PhoneNumberInput } from "@molecules/Form/Form"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"
import { MutationSuccessFunctionParams } from "@molecules/Form/GenericForm/GenericForm.types"
import toast from "@molecules/Toast/Toast"

import { useComponentNavigation } from "@organisms/ComponentNavigation/ComponentNavigation.context"
import {
    PhoneCodeRequestPayload,
    PhoneCodeRequestResponse,
} from "@organisms/SecurityCheckDialog/SecurityCheckDialog.types"

import styles from "@pages/Auth/Auth.module.scss"
import useAuth from "@pages/Auth/Auth.store"

import { AUTH_ENDPOINTS } from "@endpoints/auth"

import { ACCOUNT_VERIFICATION_TEMPORARY_CODE_ID_HOLD } from "@constants/storage"

export type PhoneSchema = {
    phone_number: User["phone"]
}

export const phoneNumberSchema: Yup.ObjectSchema<PhoneSchema> = Yup.object().shape({
    phone_number: Yup.string()
        .required("Please enter a phone number")
        .test("is-valid-phone", "Please enter a valid phone number", (value) => isValidPhoneNumber(value)),
})

export default function AuthPhoneCodeForm() {
    const { preferredAuthMethod, setPhoneData, phoneData, setIsWaitingForCodeInput, isWaitingForCodeInput } = useAuth()

    const { navigate, currentComponentId } = useComponentNavigation()
    const isOnOTPSlide = currentComponentId?.endsWith("_otp")

    const isActive = preferredAuthMethod === "phone_and_code"

    const {
        state: otpState,
        setState: setOtpState,
        isValid,
        isWithinCooldown,
    } = useOTPRateLimit(ACCOUNT_VERIFICATION_TEMPORARY_CODE_ID_HOLD)

    const onMutationSuccess = (args: MutationSuccessFunctionParams<PhoneSchema, PhoneCodeRequestResponse>) => {
        setPhoneData({
            phone: args.formValues.phone_number,
            phoneId: args.data.phone_id,
        })

        // Store OTP state with timestamp for rate limiting
        setOtpState({
            codeVerificationId: args.data.phone_id,
            sentAt: Date.now(),
            checkFor: "phone",
            contactValue: args.formValues.phone_number,
        })

        toast({
            variant: "default",
            type: "success",
            title: "Code sent",
        })

        setIsWaitingForCodeInput(true)
        navigate("phone_and_code_otp")
    }

    const onRequestPasswordInsteadClick = () => {
        navigate("phone_and_password")
    }

    // Handle click with rate limiting check - uses form value (E164 format) for comparison
    const createClickHandler = (currentPhone: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
        const hasValidExistingCode = isValid("phone", currentPhone)

        // If we have a valid code within the cooldown window, skip requesting and navigate directly
        if (hasValidExistingCode && otpState) {
            event.preventDefault()
            setPhoneData({
                phone: otpState.contactValue,
                phoneId: otpState.codeVerificationId,
            })
            setIsWaitingForCodeInput(true)
            navigate("phone_and_code_otp")
            return
        }

        // If waiting for code input but rate limit has expired, allow new request (shows toast)
        // If still within rate limit, just navigate to OTP screen
        if (isWaitingForCodeInput && isWithinCooldown) {
            event.preventDefault()
            navigate("phone_and_code_otp")
        }
    }

    return (
        <GenericForm<PhoneSchema, PhoneCodeRequestResponse, Error, PhoneCodeRequestPayload>
            formConfig={{
                defaultValues: {
                    phone_number: phoneData?.phone || "",
                },
            }}
            formValues={phoneData?.phone ? { phone_number: phoneData.phone } : undefined}
            formValidationSchema={phoneNumberSchema}
            mutationConfig={{
                method: "POST",
                endpoint: AUTH_ENDPOINTS.OTP.SMS_REQUEST,
                genericErrorMessage: "Could not request code.",
                disabled: isWithinCooldown,
            }}
            triggerFormReset={!isActive}
            onMutationSuccess={onMutationSuccess}
            className={styles.form}
        >
            {({ isPending, nonFieldError, form }) => {
                // Use form value (E164 format) for rate limiting comparison
                const currentPhone = form.watch("phone_number") || ""

                return (
                    <>
                        {nonFieldError && (
                            <AlertBanner
                                type="banner"
                                message={nonFieldError}
                                status="error"
                                className={styles.topAlertBanner}
                            />
                        )}
                        <PhoneNumberInput<PhoneSchema>
                            name="phone_number"
                            label="Phone number"
                            defaultCountryCode="US"
                            placeholder="(000) 000-0000"
                            isTabbable={isActive && !isOnOTPSlide}
                            size="md"
                        />
                        <div className={styles.formCTAs}>
                            <Button
                                type="submit"
                                size="lg"
                                variant="solid"
                                isFullWidth={true}
                                isTabbable={isActive && !isOnOTPSlide}
                                isProcessing={isPending}
                                onClick={createClickHandler(currentPhone)}
                                rightIcon={faIcon({ name: "arrow-right", style: "regular" })}
                            >
                                Get login code
                            </Button>
                            <Button
                                size="lg"
                                variant="ghost"
                                rightIcon={faIcon({ name: "arrow-right", style: "regular" })}
                                onClick={onRequestPasswordInsteadClick}
                                isTabbable={isActive && !isOnOTPSlide}
                                data-prevent-input-validation={true}
                                type="button"
                                isFullWidth={true}
                            >
                                Sign in with password
                            </Button>
                        </div>
                    </>
                )
            }}
        </GenericForm>
    )
}
